import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { redirectToLogin } from './redirectToLogin.js';
export const AuthContext = createContext({ user: null, loading: true, refresh: () => { } });
export function isPlatformUser(user) {
    return user?.platformRole === 'admin' || user?.platformRole === 'viewer';
}
// A tenant admin for UI gating: platform god-mode (admin/viewer), the owning org's
// admin, or the active tenant's admin. Mirrors the server's requireAdmin — the API
// is the real enforcement; this only gates affordances (defense-in-depth: UX only).
export function isTenantAdmin(user) {
    return isPlatformUser(user) || user?.orgRole === 'admin' || user?.tenantRole === 'admin';
}
// A channel agent: holds the compatibility `callcenter` product scope but is NOT
// any kind of admin (platform / org / tenant). Mirrors isChannelAgent in
// identity's home-resolver — such a session's home is the operator desk, so any
// management surface redirects it out. UX-only, like isTenantAdmin.
export function isChannelAgent(user) {
    if (!user)
        return false;
    return (!isPlatformUser(user) &&
        user.orgRole !== 'admin' &&
        user.tenantRole !== 'admin' &&
        !!user.productScopes?.['callcenter']);
}
/** @deprecated Use isChannelAgent; retained for consumers on the deployed scope name. */
export const isCallcenterAgent = isChannelAgent;
// The compatibility product-scope value carries the operator's channel role (Axis B):
// agent / supervisor / admin. Shown on the desk so an operator reads as
// "Channel agent", not the generic tenant "Member".
const CC_ROLE_LABEL = {
    agent: 'Channel agent',
    supervisor: 'Channel supervisor',
    admin: 'Channel admin',
};
// The one permanent-authority label shown in every shell footer. Higher scopes
// win so entering a tenant never relabels platform or org staff as merely a
// tenant admin. Current acting scope is displayed separately by the shell.
export function roleLabel(user) {
    if (user?.platformRole)
        return `Platform ${user.platformRole}`;
    if (user?.orgRole === 'admin')
        return 'Org admin';
    if (user?.tenantRole === 'admin')
        return 'Tenant admin';
    const cc = user?.productScopes?.['callcenter'];
    if (cc)
        return CC_ROLE_LABEL[cc] ?? 'Channel agent';
    if (user?.tenantRole === 'user')
        return 'Member';
    return 'Member';
}
/**
 * Resolve the two facts every control-plane shell must show: permanent
 * authority and current operating scope. Tenant membership count is supplied
 * by the caller because it is data, not a JWT claim.
 */
export function resolveShellIdentity(user, tenant, hasMultipleTenantMemberships = false) {
    const authority = roleLabel(user);
    const platform = isPlatformUser(user);
    const orgAdmin = user.orgRole === 'admin';
    const elevated = platform || orgAdmin;
    if (tenant) {
        // Org and tenant frequently share a name — "Getronics AI / Getronics AI"
        // reads as a stutter, so the org prefix only appears when it adds a fact.
        const target = tenant.orgName && tenant.orgName !== tenant.name
            ? `${tenant.orgName} / ${tenant.name}`
            : tenant.name;
        return {
            accent: 'tenant',
            authority,
            location: elevated ? `Acting in ${target}` : target,
            navGroup: { label: elevated ? 'Acting in' : 'Tenant', entity: tenant.name },
            canChooseTenant: elevated || hasMultipleTenantMemberships,
            exitPath: platform
                ? '/admin/organizations'
                : orgAdmin
                    ? user.orgId ? `/admin/organizations/${user.orgId}/overview` : '/admin'
                    : null,
        };
    }
    if (platform) {
        return {
            accent: 'platform',
            authority,
            location: 'Platform',
            navGroup: { label: 'Scope', entity: 'Platform' },
            canChooseTenant: true,
            exitPath: null,
        };
    }
    if (orgAdmin) {
        return {
            accent: 'org',
            authority,
            location: 'Organization',
            navGroup: { label: 'Scope', entity: 'Organization' },
            canChooseTenant: true,
            exitPath: null,
        };
    }
    return {
        accent: 'tenant',
        authority,
        location: 'Account',
        navGroup: { label: 'Scope', entity: 'Account' },
        canChooseTenant: hasMultipleTenantMemberships,
        exitPath: null,
    };
}
// The /api/auth/me contract in one place — every SPA's AuthProvider used to restate
// this path + response shape. Structurally typed on `{ get }` so web-auth needs no
// @retink/web-ui (Api) dependency.
export function fetchMe(api) {
    return api.get('/api/auth/me').then((r) => r.user);
}
export const AUTH_CHECK_TIMEOUT_MS = 15_000;
export function withAuthTimeout(promise, timeoutMs = AUTH_CHECK_TIMEOUT_MS) {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('Session check timed out')), timeoutMs);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
export function AuthErrorState({ onRetry, onSignIn }) {
    return (_jsx("div", { className: "flex h-full items-center justify-center bg-slate-950 px-6", children: _jsxs("div", { className: "max-w-sm text-center", role: "alert", children: [_jsx("h1", { className: "text-base font-semibold text-slate-100", children: "Couldn\u2019t confirm your session" }), _jsx("p", { className: "mt-2 text-sm text-slate-400", children: "Check your connection, then retry. You can also sign in again." }), _jsxs("div", { className: "mt-5 flex justify-center gap-3", children: [_jsx("button", { type: "button", onClick: onRetry, className: "rounded-md bg-slate-100 px-3 py-2 text-sm font-medium text-slate-950", children: "Retry" }), _jsx("button", { type: "button", onClick: onSignIn, className: "rounded-md border border-slate-700 px-3 py-2 text-sm font-medium text-slate-200", children: "Sign in again" })] })] }) }));
}
export function AuthProvider({ children, idUrl, fetchMe, timeoutMs = AUTH_CHECK_TIMEOUT_MS, unauthenticated }) {
    const [user, setUser] = useState(null);
    const [bootstrap, setBootstrap] = useState();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const fetchMeRef = useRef(fetchMe);
    const requestRef = useRef(0);
    useEffect(() => { fetchMeRef.current = fetchMe; }, [fetchMe]);
    const refresh = useCallback(() => {
        const request = ++requestRef.current;
        setLoading(true);
        setError(false);
        withAuthTimeout(Promise.resolve().then(() => fetchMeRef.current()), timeoutMs)
            .then((session) => {
            if (request !== requestRef.current)
                return;
            if (session && 'user' in session) {
                setUser(session.user);
                setBootstrap(session.bootstrap);
            }
            else {
                setUser(session);
                setBootstrap(undefined);
            }
        })
            .catch(() => { if (request === requestRef.current)
            setError(true); })
            .finally(() => { if (request === requestRef.current)
            setLoading(false); });
    }, [timeoutMs]);
    useEffect(() => {
        refresh();
        return () => { requestRef.current += 1; };
    }, [refresh]);
    useEffect(() => {
        if (!loading && !error && !user && unauthenticated === undefined)
            redirectToLogin(idUrl);
    }, [loading, error, user, idUrl, unauthenticated]);
    if (loading) {
        return (_jsx("div", { className: "flex h-full items-center justify-center bg-slate-950", children: _jsx("div", { className: "text-slate-400 text-sm", children: "Loading\u2026" }) }));
    }
    if (error)
        return _jsx(AuthErrorState, { onRetry: refresh, onSignIn: () => redirectToLogin(idUrl) });
    if (!user) {
        if (unauthenticated !== undefined)
            return unauthenticated;
        return (_jsx("div", { className: "flex h-full items-center justify-center bg-slate-950", children: _jsx("div", { className: "text-slate-400 text-sm", children: "Taking you to sign in\u2026" }) }));
    }
    return (_jsx(AuthContext.Provider, { value: { user, bootstrap, loading, refresh }, children: children }));
}
